/**
 * PageHelpButtons - Combined "Ask Support AI" and "Explain This Screen" buttons
 * P1: Provides contextual help for any page
 */

import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircleQuestion, Sparkles, HelpCircle, Info, Loader2 } from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useSupportContext } from '../context/SupportContext';
import { toast } from 'sonner';

const PageHelpButtons = ({ 
  variant = 'default', // 'default', 'compact', 'inline'
  mode = 'advisory',
  projectId = null,
  showExplain = true,
  showAssist = true,
  className = ''
}) => {
  const location = useLocation();
  const { user } = useAuth();
  const { openSupportDrawer, preferredRole } = useSupportContext();
  const [loadingExplain, setLoadingExplain] = useState(false);
  
  const tenantId = user?.tenant?.id || user?.tenant_id;
  
  const handleAskSupport = () => {
    openSupportDrawer({
      mode,
      entity: projectId ? { projectId } : null
    });
  };
  
  const handleExplainScreen = async () => {
    if (!tenantId || !user?.id) {
      toast.error('Please log in to use this feature');
      return;
    }
    
    setLoadingExplain(true);
    
    try {
      const res = await api.explainScreen(
        tenantId, 
        user.id, 
        location.pathname, 
        preferredRole || 'pm'
      );
      
      // Open drawer with the explanation
      openSupportDrawer({
        mode: 'product',
        message: res.data.answerHtml
      });
      
    } catch (err) {
      console.error('Explain screen failed:', err);
      toast.error('Failed to load page explanation');
      
      // Fallback - open drawer with generic prompt
      openSupportDrawer({
        mode: 'product',
        message: `Explain this screen: ${location.pathname}`
      });
    } finally {
      setLoadingExplain(false);
    }
  };
  
  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        {showExplain && (
          <button
            onClick={handleExplainScreen}
            disabled={loadingExplain}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
            data-testid="explain-screen-btn"
            title="Explain this screen"
          >
            {loadingExplain ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Info className="w-3 h-3" />
            )}
            <span className="hidden sm:inline">Explain</span>
          </button>
        )}
        {showAssist && (
          <button
            onClick={handleAskSupport}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-md transition-colors"
            data-testid="ask-support-btn"
            title="Ask Support AI"
          >
            <MessageCircleQuestion className="w-3 h-3" />
            <span className="hidden sm:inline">Ask AI</span>
          </button>
        )}
      </div>
    );
  }
  
  if (variant === 'inline') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {showExplain && (
          <button
            onClick={handleExplainScreen}
            disabled={loadingExplain}
            className="text-xs text-gray-500 hover:text-purple-600 underline-offset-2 hover:underline flex items-center gap-1"
            data-testid="explain-screen-btn-inline"
          >
            {loadingExplain ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <HelpCircle className="w-3 h-3" />
            )}
            Explain this screen
          </button>
        )}
        {showAssist && showExplain && <span className="text-gray-300">|</span>}
        {showAssist && (
          <button
            onClick={handleAskSupport}
            className="text-xs text-purple-600 hover:text-purple-700 underline-offset-2 hover:underline flex items-center gap-1"
            data-testid="ask-support-btn-inline"
          >
            <Sparkles className="w-3 h-3" />
            Ask Support AI
          </button>
        )}
      </div>
    );
  }
  
  // Default variant - button group
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {showExplain && (
        <button
          onClick={handleExplainScreen}
          disabled={loadingExplain}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
          data-testid="explain-screen-btn-default"
        >
          {loadingExplain ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Info className="w-4 h-4" />
          )}
          Explain
        </button>
      )}
      {showAssist && (
        <button
          onClick={handleAskSupport}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors"
          data-testid="ask-support-btn-default"
        >
          <Sparkles className="w-4 h-4" />
          Ask AI
        </button>
      )}
    </div>
  );
};

export default PageHelpButtons;
